import { useEffect, useState } from 'react';
import { assinaLaudos } from '@/lib/roles';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import {
  BellRing,
  Building2,
  Coins,
  ScrollText,
  UsersRound,
  FilePlus,
  LayoutGrid,
  Settings,
  Stamp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { ContadorDeAvisos } from '@/components/Avisos/ContadorDeAvisos';
import { useAvisosNaoLidos } from '@/components/Avisos/useAvisosNaoLidos';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { getAvisosDaEquipe } from '@/lib/avisos_da_equipe';
import { logout as httpLogout } from '@/lib/auth';
import { getPerfil } from '@/lib/perfil';
import { useSessionStore } from '@/stores/session';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useBarraRecolhidaNaMesa } from './barraRecolhida';
import { AlternarABarra, ContaDaBarra, MarcaDaBarra } from './PartesDaBarra';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  badge?: string | number;
  end?: boolean;
  /** E150.F1: o item mostra o número de avisos não lidos. */
  contador?: boolean;
}

// E150.F1 (GUS-150): os avisos da equipe — o que os clientes registraram na conversa dos casos.
const WORK_ITEMS: readonly NavItem[] = [
  { to: '/casos', label: ptBR.shell.sidebar.casos, Icon: LayoutGrid, end: true },
  { to: '/casos/novo', label: ptBR.shell.sidebar.novoCaso, Icon: FilePlus },
  { to: '/avisos', label: ptBR.avisosDaEquipe.menu, Icon: BellRing, end: true, contador: true },
];

const ADMIN_ITEMS: readonly NavItem[] = [
  { to: '/perfil', label: ptBR.shell.sidebar.perfil, Icon: User },
  { to: '/config', label: ptBR.shell.sidebar.config, Icon: Settings },
];

// Só o escritório-plataforma (SignCheck Admin) vê o console e a configuração
// comercial. O gate é o mesmo do servidor (`is_platform` resolvido no perfil);
// esconder o item é conforto — quem digitar a URL recebe /403 e, na porta do
// servidor, 403 PLATFORM_REQUIRED.
const PLATFORM_ITEMS: readonly NavItem[] = [
  { to: '/admin/clientes', label: ptBR.shell.sidebar.adminClientes, Icon: Building2 },
  { to: '/admin/usuarios', label: ptBR.shell.sidebar.adminUsuarios, Icon: UsersRound },
  { to: '/admin/trilha', label: ptBR.shell.sidebar.adminTrilha, Icon: ScrollText },
  { to: '/configuracoes/precificacao', label: ptBR.shell.sidebar.precificacao, Icon: Coins },
];

// E52.F3: a tela de certificação exige plataforma **e** papel certificador —
// o mesmo gate composto do servidor. O titular da plataforma não a vê, porque
// não herda o poder de assinatura que ela antecede (BR-08).
const CERTIFICACAO_ITEM: NavItem = {
  to: '/certificacao',
  label: ptBR.shell.sidebar.certificacao,
  Icon: Stamp,
};

function userInitials(identity: string | null | undefined): string {
  if (!identity) return '—';
  // Nome de pessoa ("João Silva") → "JS"; e-mail → iniciais do local-part.
  const base = identity.includes('@') ? (identity.split('@')[0] ?? '') : identity;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const a = parts[0]?.[0];
  const b = parts[1]?.[0];
  if (a && b) return (a + b).toUpperCase();
  return base.slice(0, 2).toUpperCase() || '—';
}

/**
 * Identidade exibida no bloco da conta, em três degraus.
 *
 * E30.F1: o nome do perito titular substituiu o rótulo estático "Perito
 * grafotécnico", com o e-mail como fallback (e como linha secundária quando há
 * nome). E56.F1.T03: o nome da PESSOA passa à frente do titular do ESCRITÓRIO —
 * num escritório com mais de uma pessoa, o titular não é quem está logado.
 *
 * A normalização de vazio/whitespace vale nos dois degraus de nome, pela razão
 * da E30.F1.C01 (F-E30.F1-01): `""` passaria como nome válido e o bloco
 * renderizaria em branco em vez de cair no degrau seguinte.
 */
function resolverIdentidade(
  nomePessoa: string | null,
  peritoTitular: string | null,
  email: string | null,
): { nomeResolvido: string | null; displayName: string | null } {
  const nomeResolvido = (nomePessoa?.trim() || null) ?? (peritoTitular?.trim() || null);
  return { nomeResolvido, displayName: nomeResolvido ?? email };
}

export function Sidebar() {
  const token = useSessionStore((s) => s.token); // ux-neutrality:allow auth-token
  const clearToken = useSessionStore((s) => s.clearToken); // ux-neutrality:allow auth-token
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [peritoTitular, setPeritoTitular] = useState<string | null>(null);
  const [nomePessoa, setNomePessoa] = useState<string | null>(null);
  const [isPlatform, setIsPlatform] = useState(false);
  const [isCertificador, setIsCertificador] = useState(false);
  const [bancoAtivo, setBancoAtivoLocal] = useState<string | null>(null);
  // E205.F1.T05: a escolha da barra na mesa é por pessoa.
  const [pessoa, setPessoa] = useState<string | null>(null);
  const naMesa = useMatch('/casos/:id/mesa') !== null;
  const barra = useBarraRecolhidaNaMesa(naMesa, pessoa);
  const recolhida = barra.recolhida;
  const naoLidos = useAvisosNaoLidos(getAvisosDaEquipe, Boolean(token)); // ux-neutrality:allow auth-token

  useEffect(() => {
    if (!token /* ux-neutrality:allow auth-token */) {
      setPessoa(null);
      setEmail(null);
      setPeritoTitular(null);
      setNomePessoa(null);
      setIsPlatform(false);
      setIsCertificador(false);
      return;
    }
    let cancelled = false;
    getPerfil(apiClient)
      .then((p) => {
        if (!cancelled) {
          setPessoa(p.id);
          setEmail(p.email);
          setPeritoTitular(p.perito_titular);
          setNomePessoa(p.display_name);
          setIsPlatform(p.is_platform);
          setIsCertificador(assinaLaudos(p.role, p.is_platform));
          // E57.F2: o contexto vem do SERVIDOR, já conferido contra o vínculo.
          setBancoAtivoLocal(p.banco_ativo);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEmail(null);
          setPeritoTitular(null);
          setNomePessoa(null);
          setIsPlatform(false);
          setIsCertificador(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, token]); // ux-neutrality:allow auth-token

  const { nomeResolvido, displayName } = resolverIdentidade(nomePessoa, peritoTitular, email);

  async function handleLogout() {
    const current = token; // ux-neutrality:allow auth-token
    clearToken(); // ux-neutrality:allow auth-token
    if (current) {
      try {
        await httpLogout(apiClient, current);
      } catch {
        // logout server-side é best-effort
      }
    }
    navigate('/login', { replace: true });
  }

  return (
    <aside
      id="app-sidebar"
      aria-label={ptBR.shell.sidebar.sectionMain}
      data-recolhida={recolhida}
      className={cn(
        'flex w-full shrink-0 flex-col gap-[26px] border-b border-border bg-bg py-[22px] md:sticky md:top-0 md:h-screen md:border-b-0 md:border-r',
        recolhida ? 'px-4 md:w-14 md:px-2' : 'px-4 md:w-[236px]',
      )}
    >
      <MarcaDaBarra recolhida={recolhida} />
      {barra.recolhivel ? <AlternarABarra barra={barra} /> : null}

      <TooltipProvider delayDuration={200}>
        <SidebarSection
          label={ptBR.shell.sidebar.sectionWork}
          items={WORK_ITEMS}
          naoLidos={naoLidos}
          semRotulo
          recolhida={recolhida}
        />
        <SidebarSection
          label={ptBR.shell.sidebar.sectionAdmin}
          items={ADMIN_ITEMS}
          recolhida={recolhida}
        />
        {isPlatform ? (
          <SidebarSection
            label={ptBR.shell.sidebar.sectionPlataforma}
            items={isCertificador ? [...PLATFORM_ITEMS, CERTIFICACAO_ITEM] : PLATFORM_ITEMS}
            recolhida={recolhida}
          />
        ) : null}
      </TooltipProvider>

      <ContaDaBarra
        recolhida={recolhida}
        bancoAtivo={bancoAtivo}
        iniciais={userInitials(displayName)}
        displayName={displayName}
        email={nomeResolvido ? email : null}
        onSair={token /* ux-neutrality:allow auth-token */ ? handleLogout : null}
      />
    </aside>
  );
}

/**
 * Item de navegação da prancha 1B: 14px, respiro 7px/10px e **borda hairline
 * sempre presente** — transparente em repouso, na cor da divisória quando
 * ativo. A borda constante é o que impede o item de "pular" 1px ao virar
 * ativo, e é como o `.sc-nav a` do pacote é construído.
 *
 * O ativo é campo de aço claro (`accent-100` + `accent-800`), não uma barra
 * lateral: no blueprint quem marca posição é a moldura, não um enfeite.
 */
const ITEM_BASE =
  'flex items-center gap-2.5 border px-2.5 py-[7px] text-[14px] transition-colors duration-fast ease-sc focus-visible:outline-2 focus-visible:outline-accent';
const ITEM_ATIVO = 'border-border bg-accent-100 text-accent-800';
const ITEM_REPOUSO = 'border-transparent text-fg hover:bg-fg/[0.05]';
/**
 * E207.F5.T01 (owner, 01/11): na barra recolhida (um trilho de 56 px, 40 px de dentro) a marca, o
 * botão, cada item, a conta e "Sair" se centram no mesmo eixo, sem o respiro lateral — o ícone de um
 * item saía deslocado do ícone do botão e o número de avisos estourava o trilho. (Recolhida, os itens
 * chegavam sem classe nenhuma: ver `ItemDaBarra`.)
 */
export const RECOLHIDA_NO_EIXO = 'relative md:justify-center md:px-0';

function ItemDaBarra({
  item,
  naoLidos,
  recolhida,
}: {
  item: NavItem;
  naoLidos: number | null;
  recolhida: boolean;
}) {
  // E207.F5.T01: a classe vai como texto, não como função. O gatilho da dica (`asChild`) junta a
  // classe do filho como texto, e a função virava o próprio código no `class`: recolhida, cada item
  // perdia flex, borda e respiro (a causa da bagunça relatada). O ativo sai do mesmo casamento de
  // rota que o NavLink faz; o `aria-current` segue com ele.
  const ativo = useMatch({ path: item.to, end: item.end ?? false }) !== null;
  const link = (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      className={cn(ITEM_BASE, ativo ? ITEM_ATIVO : ITEM_REPOUSO, recolhida && RECOLHIDA_NO_EIXO)}
    >
      <item.Icon className="h-[17px] w-[17px] shrink-0" aria-hidden="true" />
      <span className={cn('flex-1 truncate', recolhida && 'md:sr-only')}>{item.label}</span>
      {item.contador ? (
        // Recolhida, o número vai ao canto do ícone: ao lado ele estourava o trilho.
        <span
          className={cn('ml-auto flex', recolhida && 'md:absolute md:right-0.5 md:top-0.5 md:ml-0')}
          data-testid="menu-avisos-posicao"
        >
          <ContadorDeAvisos naoLidos={naoLidos} rotulo={ptBR.avisosDaEquipe.naoLidosNoMenu} />
        </span>
      ) : null}
      {item.badge !== undefined ? (
        <span className="font-mono text-[10px] text-fg-muted">{item.badge}</span>
      ) : null}
    </NavLink>
  );
  if (!recolhida) return link;
  // Recolhida, o nome do item aparece ao passar o ponteiro ou o foco (e segue no leitor de tela).
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function SidebarSection({
  label,
  items,
  naoLidos = null,
  semRotulo = false,
  recolhida = false,
}: {
  label: string;
  items: readonly NavItem[];
  naoLidos?: number | null;
  /** E163.F3 (A67-18): a primeira seção do molde não tem rótulo visível; o nome fica no `nav`. */
  semRotulo?: boolean;
  /** E205.F1.T05: na mesa, só os ícones, com o nome no `Tooltip`. */
  recolhida?: boolean;
}) {
  return (
    <nav className="flex flex-col gap-0.5" aria-label={label}>
      {semRotulo ? null : (
        <p
          className={cn(
            'px-2.5 pb-1.5 font-titulo text-rotulo uppercase text-fg-dim',
            recolhida && 'md:sr-only',
          )}
        >
          {label}
        </p>
      )}
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <ItemDaBarra item={item} naoLidos={naoLidos} recolhida={recolhida} />
          </li>
        ))}
      </ul>
    </nav>
  );
}
